import { WorkflowContainerFragment } from 'generated/resources/graphql'

type WorkflowNodeFile = WorkflowContainerFragment['workflowNodeFiles'][number]
type WorkflowNode = WorkflowContainerFragment['workflowNodes'][number]

/**
 * Map Workflow Node Files to Workflow Nodes
 *
 * Map keys:
 * - Always the node `identifier` (same value as `NodeUI.id` for action/trigger/condition nodes)
 *
 * Cases:
 * - Files with `workflowNodeId` (persisted nodes):
 *   - Resolved to their `identifier` using `workflowNodes`.
 * - Files with only `nodeIdentifier` (pending association / local nodes):
 *   - Used directly as the key.
 */
export function mapWorkflowNodeFilesToNodes(
  workflowNodeFiles: WorkflowNodeFile[],
  workflowNodes: WorkflowNode[]
): Map<string, WorkflowNodeFile[]> {
  // Auxiliary map: workflow_node_id (DB) -> node identifier
  const identifierByWorkflowNodeId = new Map<string, string>()
  for (const node of workflowNodes) {
    if (node.id != null && node.identifier) {
      identifierByWorkflowNodeId.set(node.id, node.identifier)
    }
  }

  return workflowNodeFiles.reduce<Map<string, WorkflowNodeFile[]>>((filesByNodeId, file) => {
    let nodeIdentifierKey: string | undefined

    if (file.workflowNodeId != null) {
      // Persisted node: resolve its identifier from the numeric id
      nodeIdentifierKey = identifierByWorkflowNodeId.get(file.workflowNodeId) ?? file.workflowNodeId
    } else if (file.nodeIdentifier) {
      // Local / pending node: use nodeIdentifier directly
      nodeIdentifierKey = file.nodeIdentifier
    }

    if (!nodeIdentifierKey) return filesByNodeId

    const existing = filesByNodeId.get(nodeIdentifierKey)
    if (existing) {
      existing.push(file)
    } else {
      filesByNodeId.set(nodeIdentifierKey, [file])
    }

    return filesByNodeId
  }, new Map<string, WorkflowNodeFile[]>())
}
